/**
 * Jóváhagyásra vár — the review queue of handover rev7 §11.7.
 *
 * Three actions, as §11.7 lists them: approve, propose amendment, decline with
 * reason. Each is a `console_*` function with its own role gate; this screen
 * does not decide who may press a button, it shows the server's answer when
 * somebody does.
 *
 * The wait is in working hours (§11.4), and the same reading the C3 and C4
 * sweep uses, so an order the console says has waited two hours is an order
 * the reminder agrees has waited two hours.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import {
  asDate,
  asMoment,
  fmt,
  Loading,
  REVIEW_REASON_HU,
  Tags,
  useConsoleAction,
  useConsoleData,
} from '../common.tsx'

interface QueueOrder {
  id: string
  ref: string
  event_name: string | null
  event_date: string
  guests: number
  gf_guests: number
  customer_name: string
  customer_company: string | null
  gross: number
  review_reasons: string[]
  dietary_note: string | null
  entered_review_at: string | null
  waiting_working_h: number
  needs_dietary_signoff: boolean
  dietary_signoff: { decision: string; resolution: string } | null
}

interface Queue {
  orders: QueueOrder[]
  reminder_working_h: number | null
  escalation_working_h: number | null
}

export function Review(): JSX.Element {
  const { data, error, loading, reload } = useConsoleData<Queue>('review_queue', {})
  const { run, busy, error: actionError, moved } = useConsoleAction()
  const [open, setOpen] = useState<{ id: string; kind: 'decline' | 'amend' } | null>(null)
  const [reason, setReason] = useState('')

  async function act(action: string, args: Record<string, unknown>): Promise<void> {
    const ok = await run(action, args)
    if (ok) {
      setOpen(null)
      setReason('')
    }
    // §19.2 principle 2 commits the database half of §9.6 before the provider
    // is asked, so a refusal is not always a no-op: `review_decline` cancels
    // the order and only then asks for the hold to be released. Reload on a
    // refusal the endpoint marked `order_moved`, or the cancelled order stays
    // in the queue and the next colleague declines it a second time. A refused
    // capture is not marked, because ruling 4 and §9.10 no. 14 leave the order
    // where it was — and the sentence above the queue has to stay readable
    // beside the row it is about.
    if (ok || moved.current) reload()
  }

  const escalation = data?.escalation_working_h ?? null
  const reminder = data?.reminder_working_h ?? null

  return (
    <section data-testid="view-review">
      <h2>Jóváhagyásra vár</h2>
      <p className="lead">
        §11.4: a 15:00 előtt beérkező jóváhagyásokat aznap 16:00-ig, a későbbieket a következő
        munkanap 16:00-ig válaszoljuk meg.
        {reminder !== null && escalation !== null
          ? ` Emlékeztető ${fmt.n(reminder)}, eszkaláció ${fmt.n(escalation)} munkaóra után.`
          : ''}
      </p>

      <Loading loading={loading} error={error} />
      {actionError !== null && <p className="note error" data-testid="action-error">{actionError}</p>}

      {data !== null && data.orders.length === 0 && (
        <p className="note" data-testid="review-empty">Nincs jóváhagyásra váró rendelés.</p>
      )}

      {data !== null && data.orders.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Rendelés</th>
              <th>Rendezvény</th>
              <th className="num">Vendég</th>
              <th>Ok</th>
              <th className="num">Várakozás</th>
              <th>Művelet</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((order) => {
              const late = escalation !== null && order.waiting_working_h >= escalation
              const due = !late && reminder !== null && order.waiting_working_h >= reminder
              return (
                <tr key={order.id} data-testid={`review-${order.ref}`}>
                  <td>
                    <Link to={`/staff/orders/${order.id}`}>{order.ref}</Link>
                    <div className="tag">{order.customer_company ?? order.customer_name}</div>
                  </td>
                  <td>
                    {asDate(order.event_date, true)}
                    {order.event_name !== null && <div className="tag">{order.event_name}</div>}
                  </td>
                  <td className="num">
                    {fmt.n(order.guests)}
                    {order.gf_guests > 0 ? ` (${fmt.n(order.gf_guests)} GM)` : ''}
                  </td>
                  <td>
                    <Tags values={order.review_reasons.map((r) => REVIEW_REASON_HU[r] ?? r)} />
                    {order.needs_dietary_signoff && (
                      <div className="tag stop" data-testid={`needs-signoff-${order.ref}`}>
                        aláírás nélkül nem hagyható jóvá
                      </div>
                    )}
                  </td>
                  <td className="num">
                    <span className={late ? 'tag stop' : due ? 'tag warn' : 'tag'}>
                      {fmt.n(order.waiting_working_h, 1)} munkaóra
                    </span>
                    <div className="tag">{asMoment(order.entered_review_at)}</div>
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        type="button"
                        className="primary"
                        disabled={busy}
                        onClick={() => void act('review_approve', { order_id: order.id })}
                        data-testid={`approve-${order.ref}`}
                      >
                        Jóváhagyás
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setOpen({ id: order.id, kind: 'amend' })}
                        data-testid={`amend-${order.ref}`}
                      >
                        Módosítási javaslat
                      </button>
                      <button
                        type="button"
                        className="danger"
                        disabled={busy}
                        onClick={() => setOpen({ id: order.id, kind: 'decline' })}
                        data-testid={`decline-${order.ref}`}
                      >
                        Elutasítás
                      </button>
                    </div>

                    {open?.id === order.id && (
                      <div className="card" data-testid={`form-${open.kind}-${order.ref}`}>
                        <h3>
                          {open.kind === 'decline'
                            ? 'Elutasítás indoka'
                            : 'Mit javasolunk a vendégnek?'}
                        </h3>
                        <label htmlFor={`reason-${order.id}`}>
                          {open.kind === 'decline'
                            ? 'Ez az indok megy ki a vendégnek (U6).'
                            : 'Ez a mondat megy ki a vendégnek (U4). A módosított verziót a rendelésszerkesztőben kell összeállítani (§11.11).'}
                        </label>
                        <textarea
                          id={`reason-${order.id}`}
                          rows={3}
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                          data-testid="reason"
                        />
                        <div className="actions">
                          <button
                            type="button"
                            className="primary"
                            disabled={busy || reason.trim() === ''}
                            onClick={() =>
                              void act(
                                open.kind === 'decline' ? 'review_decline' : 'review_amend',
                                { order_id: order.id, reason },
                              )}
                            data-testid="reason-send"
                          >
                            Küldés
                          </button>
                          <button type="button" onClick={() => setOpen(null)}>Mégse</button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}
